import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, CornerDownRight, ExternalLink, Pause, Repeat, Route, Trash2, Undo2, Wrench } from 'lucide-react'
import type { Goal, GoalBoardCard } from '../../services/goalService'
import { goalState, targetPassed } from '../../utils/goalSections'
import { nextTitleWords, nextWhenWords, progressWords } from '../../utils/goalWords'
import { lifeAreaLabel } from '../../utils/taskForm'
import { ActionMenu } from '../ui/ActionMenu'
import { Timestamp } from '../ui/Timestamp'
import { Last14Practice } from '../skills/SkillKeepingUp'

/**
 * One row of the goal list (Goals UX pass 2026-09-06). A progress mark you
 * can tap to finish the goal, a title, and one quiet line: the next thing to
 * do (a task with its slot, a habit's next time, a skill's next), how far
 * along, what got done lately, what it sits under. Everything else under ⋯.
 */
interface Props {
  goal: Goal
  card: GoalBoardCard | null | undefined
  showParent?: boolean
  showArea?: boolean
  timeHint?: 'created' | 'updated' | null
  tz?: string | null
  onComplete: (goal: Goal) => void | Promise<void>
  onReopen: (goal: Goal) => void | Promise<void>
  onDelete: (goal: Goal) => void
  diaryMode?: boolean
}

function ProgressMark({ pct, size = 32 }: { pct: number; size?: number }) {
  const r = (size - 6) / 2
  const c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="3" fill="none" className="stroke-soft" />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="3" fill="none" strokeDasharray={`${(Math.max(0, Math.min(100, pct)) / 100) * c} ${c}`} strokeLinecap="round" className="stroke-brand" />
    </svg>
  )
}

export function GoalRow({ goal, card, showParent = false, showArea = false, timeHint = null, tz, onComplete, onReopen, onDelete, diaryMode = false }: Props) {
  const navigate = useNavigate()
  const href = `/goals/${goal.id}`
  const state = goalState(goal)
  const finished = state.key === 'done' || state.key === 'let_go'
  const resting = state.key === 'paused'
  const live = !finished && !resting
  const pad2 = (n: number) => String(n).padStart(2, '0')
  const todayKey = (() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` })()
  const checkWords = card?.review ? 'Ready to check' : card?.path?.status === 'completed' ? 'Journey complete' : targetPassed(goal, todayKey) ? 'Its date has passed' : null
  const area = goal.life_area || goal.parent?.life_area || null
  const pct = Math.round(Number(goal.progress || 0))
  const nextWhen = nextWhenWords(card?.next, tz)

  const menuItems = [
    { label: 'Open', icon: <ExternalLink className="h-4 w-4" />, onSelect: () => navigate(href), testId: 'goal-row-open' },
    ...(finished ? [{ label: 'Reopen', icon: <Undo2 className="h-4 w-4" />, onSelect: () => void onReopen(goal), testId: 'goal-row-reopen' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => onDelete(goal), danger: true, testId: 'goal-row-delete' },
  ]

  const mark = live ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); void onComplete(goal) }}
      aria-label={`Mark done: ${goal.title}`}
      title="Mark done"
      data-testid="goal-row-done"
      className="group/mark relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-ok/10 lg:h-9 lg:w-9"
    >
      <span className="group-hover/mark:hidden"><ProgressMark pct={pct} /></span>
      <CheckCircle2 className="hidden h-5 w-5 text-ok group-hover/mark:block" aria-hidden="true" />
      {pct > 0 && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-app-nano font-semibold text-secondary group-hover/mark:hidden">{pct}</span>}
    </button>
  ) : (
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center lg:h-9 lg:w-9" aria-hidden="true">
      {state.key === 'done' ? <CheckCircle2 className="h-5 w-5 text-ok" /> : state.key === 'let_go' ? <span className="text-soft">—</span> : <Pause className="h-4 w-4 text-muted" />}
    </span>
  )

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-lg border px-1.5 py-1 transition-colors sm:gap-2 sm:px-2 ${
        diaryMode ? 'border-warn bg-warn/20 hover:bg-warn/30' : 'border-soft bg-surface hover:border-brand'
      } ${finished ? 'opacity-75' : ''}`}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,[role=menu],[role=dialog]')) navigate(href) }}
      data-testid="goal-row"
      data-state={state.key}
    >
      {mark}
      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <Link to={href} onClick={(e) => e.stopPropagation()} className={`max-w-full truncate font-semibold hover:text-brand ${live ? 'text-primary' : 'text-muted'} ${diaryMode ? 'font-handwritten text-base sm:text-lg' : 'text-app-body'}`} title={goal.description || undefined} data-testid="goal-row-title">
            {goal.title}
          </Link>
          {state.words && <span className="hidden shrink-0 rounded bg-surface-alt px-1.5 py-0.5 text-app-micro font-medium text-secondary sm:inline" data-testid="goal-row-state">{state.words}</span>}
          {live && checkWords && <span className="shrink-0 rounded bg-brand-subtle px-1.5 py-0.5 text-app-micro font-medium text-brand" data-testid="goal-row-review">{checkWords}</span>}
          {live && card?.today && <span className="shrink-0 text-app-micro font-medium text-ok" data-testid="goal-row-today">something done today</span>}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-2 overflow-hidden whitespace-nowrap text-app-micro text-muted sm:gap-x-2.5" data-testid="goal-row-meta">
          {state.words && <span className="shrink-0 sm:hidden">{state.words}</span>}
          {live && card?.next && (
            <span className="inline-flex min-w-0 items-center gap-1 truncate font-medium text-brand" title={nextTitleWords(card.next) || undefined} data-testid="goal-row-next">
              {card.next.kind === 'habit' && <Repeat className="h-3 w-3 shrink-0" aria-hidden="true" />}
              {card.next.kind === 'skill' && <Wrench className="h-3 w-3 shrink-0" aria-hidden="true" />}
              next: {nextTitleWords(card.next)}{nextWhen ? ` · ${nextWhen}` : ''}
            </span>
          )}
          {live && !card?.next && card?.path && <span className="shrink-0 inline-flex items-center gap-1"><Route className="h-3 w-3" aria-hidden="true" />journey {card.path.status === 'completed' ? 'complete' : `${card.path.done} of ${card.path.total}`}</span>}
          {live && !card?.next && !card?.path && progressWords(goal.progress) && <span className="shrink-0">{progressWords(goal.progress)}</span>}
          {live && !card?.next && !card?.path && !progressWords(goal.progress) && card?.words && <span className="shrink-0">{card.words}</span>}
          {state.key === 'done' && goal.completed_at && <span className="shrink-0">done <Timestamp value={goal.completed_at} mode="datetime" /></span>}
          {live && card && card.days_active > 0 && <span className="hidden shrink-0 md:inline-flex"><Last14Practice days={card.last_14} size="sm" /></span>}
          {showParent && goal.parent?.title && (
            <span className="hidden min-w-0 items-center gap-1 truncate md:inline-flex" title={goal.parent.title}><CornerDownRight className="h-3 w-3 shrink-0" aria-hidden="true" />{goal.parent.title}</span>
          )}
          {showArea && area && <span className="hidden shrink-0 md:inline">{lifeAreaLabel(area)}</span>}
          {timeHint && <span className="shrink-0" data-testid="goal-row-time-hint">{timeHint === 'created' ? 'added' : 'updated'} <Timestamp value={timeHint === 'created' ? (goal.created_at || goal.updated_at || '') : (goal.updated_at || goal.created_at || '')} mode="datetime" /></span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {live && !card?.path && !card?.next && (
          <Link to={href} onClick={(e) => e.stopPropagation()} className="hidden rounded-md px-2 py-1 text-app-micro font-medium text-brand hover:bg-brand-subtle lg:inline-flex" data-testid="goal-row-plan">Plan the journey</Link>
        )}
        <ActionMenu compact label={`More for ${goal.title}`} items={menuItems} testId="goal-row-more" />
      </div>
    </div>
  )
}

export default GoalRow
